import React, { useMemo, useContext, ComponentType, ReactNode, ReactElement } from 'react';
import isEmpty from 'lodash/isEmpty';

import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import Loader from '../Loader';
import DataErrorMessage, { DataErrorMessageProps } from '../../message/DataErrorMessage';
import NoDataMessage from '../../message/NoDataMessage';
import useStill from '../../../hooks/_useStill';
import LoadBufferContext from '../_LoadBufferContext';

export interface DataLoaderRenderPropValues<Data, ResponseData = unknown> {
  data: Data;
  resourceId?: number;
  responseData?: ResponseData;
}

export interface DataLoaderProps<Data, E = {}, ResponseData = unknown> extends Omit<DataErrorMessageProps<E>, 'inline' | 'children'> {
  /** Function that will receive an object with a `data` property containing the loaded data. */
  children: (values: DataLoaderRenderPropValues<Data, ResponseData>) => ReactNode;
  /** React Element, if null `<Loader />` will be used by default. */
  CustomLoader?: ComponentType<{ content: ReactNode }> & { contentLoader?: boolean };
  /** Data being loaded */
  data?: Data;
  /** Text for the no error data. */
  noDataMessage?: string;
  /** The data is a list of data instead of a single data. Usually returned from a ResourceGroup/ResourceList. */
  isDataList?: boolean;
  /** Component to use as the NoDataMessage. */
  CustomNoDataMessage?: ComponentType<{ children: ReactNode }>;
  /** Display the error message inline. */
  inlineErrorMessage?: boolean;
  /** Force the error message to always display vertically. */
  verticalErrorMessage?: boolean;
  /** Component to use as error message. */
  CustomDataErrorMessage?: ComponentType<any>;
  /** Indicates that the data is grouped. */
  isGroup?: boolean;
}

const DataLoaderDefaultProps = {
  inlineErrorMessage: true,
};

const DataLoader = <Data, E = {}, ResponseData = unknown>({
  children, CustomLoader, pending, error, data, attempts, maxAttempts, noDataMessage, networkErrorMessage, badRequestMessage, serverErrorMessage, tryAgainMessage, retryButton, CustomNoDataMessage, inlineErrorMessage, verticalErrorMessage, serviceUnavailableMessage, tooManyRequestsMessage, buttonAlign, CustomDataErrorMessage, isDataList, isGroup, onRetry,
}: DataLoaderProps<Data, E, ResponseData>): ReactElement => {
  const { registerLoader } = useContext(LoadBufferContext);
  const min = useMemo(() => registerLoader ? 0 : undefined, [registerLoader]);
  const stillPending = useStill(pending, { min });

  const showEmptyMessage = useMemo(() => {
    if (noDataMessage === null) return false;
    if (isDataList) return (data as Data[]).every((datum) => Array.isArray(data) && isEmpty(datum));
    return Array.isArray(data) && isEmpty(data);
  }, [data, isDataList, noDataMessage]);

  const showContent = useMemo(() => {
    if (isDataList) return true;
    if (isGroup) return data === null || (typeof data !== 'undefined' && Object.values(data).every((datum) => datum !== null));
    return typeof data !== 'undefined' && data !== null;
  }, [data, isDataList, isGroup]);

  if (error) {
    if (CustomDataErrorMessage) {
      return <CustomDataErrorMessage />;
    }
    return (
      <DataErrorMessage<E>
        error={error}
        pending={pending}
        onRetry={onRetry}
        attempts={attempts}
        maxAttempts={maxAttempts}
        networkErrorMessage={networkErrorMessage}
        badRequestMessage={badRequestMessage}
        serverErrorMessage={serverErrorMessage}
        serviceUnavailableMessage={serviceUnavailableMessage}
        tooManyRequestsMessage={tooManyRequestsMessage}
        tryAgainMessage={tryAgainMessage}
        retryButton={retryButton}
        inline={inlineErrorMessage}
        vertical={verticalErrorMessage}
        buttonAlign={buttonAlign}
      />
    );
  }

  if (stillPending) {
    if (CustomLoader) {
      return <CustomLoader content={CustomLoader.contentLoader && children({ data })} />;
    }

    return <Loader />;
  }

  if (showEmptyMessage) {
    if (CustomNoDataMessage) {
      return <CustomNoDataMessage>{noDataMessage}</CustomNoDataMessage>;
    }

    return <NoDataMessage>{noDataMessage}</NoDataMessage>;
  }

  if (showContent) {
    return <>{children({ data })}</>;
  }

  return null;
};

DataLoader.defaultProps = DataLoaderDefaultProps;
DataLoader.publicProps = ['data', 'pending', 'error', 'attempts', 'maxAttempts', 'onRetry', 'noDataMessage', 'networkErrorMessage', 'badRequestMessage', 'serviceUnavailableMessage', 'tooManyRequestsMessage', 'serverErrorMessage', 'tryAgainMessage', 'retryButton', 'CustomNoDataMessage', 'CustomDataErrorMessage', 'inlineErrorMessage', 'verticalErrorMessage', 'buttonAlign'] as StringKeyOf<DataLoaderProps<{}>>[];

export default DataLoader;
